/** A step that waits for a person: what it wants, how risky, why it asks, and the answer. */
import { ChevronRight } from '@orgus/ui/icons';
import { useState } from 'react';
import { ROLE_CEILING, TIER_DESCRIPTION, TIER_LABEL, tierRank } from '@orgus/protocol';
import type { AssistantEntry, AssistantStep, Role } from '@orgus/protocol';
import { Button, Input, Pressable, RelativeTime, WorkStateIcon, cn, toUiError } from '@orgus/ui';
import { useMe } from '../auth/useMe.ts';
import { decideStep } from './data.ts';
import { stepInputJson, stepInputSummary } from './model.ts';

/** May the signed-in person answer a step of this tier? Unknown (no sign-in on this hub): yes, the hub decides. */
function useCanAnswer(step: AssistantStep): boolean {
  return useMe((s) => {
    if (s.status === 'unsupported') return true;
    const role = s.me?.actor.role as Role | undefined;
    if (!role) return false;
    return tierRank(ROLE_CEILING[role] ?? 'read') >= tierRank(step.tier);
  });
}

export interface StepAnswer {
  note: string;
  setNote: (v: string) => void;
  busy: 'allow' | 'deny' | null;
  error: string | null;
  canAnswer: boolean;
  answer: (a: 'allow' | 'deny') => Promise<boolean>;
}

/** The answer to one waiting step, shared by the conversation's card and the Inbox panel. */
export function useStepAnswer(conversationId: string, step: AssistantStep): StepAnswer {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'allow' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canAnswer = useCanAnswer(step);
  const approvalId = step.approval?.id;
  const answer = async (a: 'allow' | 'deny') => {
    if (!approvalId) return false;
    setBusy(a);
    setError(null);
    try {
      await decideStep(conversationId, approvalId, a, note);
      setNote('');
      return true;
    } catch (e) {
      const u = toUiError(e);
      setError([u.what, u.why, u.next].filter(Boolean).join(' '));
      return false;
    } finally {
      setBusy(null);
    }
  };
  return { note, setNote, busy, error, canAnswer, answer };
}

/** "Elevated" steps and up are named in the card; read and operate need no word. */
export function tierWord(step: Pick<AssistantStep, 'tier'>): string {
  return `${TIER_LABEL[step.tier]} change`;
}

/** Why a person whose role is too low cannot answer. */
export function tierBlockedText(step: Pick<AssistantStep, 'tier'>): string {
  return `${TIER_LABEL[step.tier]} steps need an owner or admin.`;
}

/**
 * The step card in a conversation: one calm surface with the ask, the exact input one click
 * away, an optional note and the two answers. Amber only on its state, never on its frame.
 */
export function ApprovalStep({ conversationId, entry }: { conversationId: string; entry: AssistantEntry }) {
  const step = entry.step!;
  const s = useStepAnswer(conversationId, step);
  const [showInput, setShowInput] = useState(false);
  const headingId = `approval-${entry.id}`;
  const summary = stepInputSummary(step.input);

  return (
    <section aria-labelledby={headingId} className="motion-arrive flex flex-col gap-4 rounded-card border border-border bg-surface p-4 shadow-elevation-flat" data-approval={entry.id}>
      <div className="flex min-w-0 items-center gap-2 text-caption">
        <WorkStateIcon state="needs-you" />
        <span className="font-medium text-status-attention">Needs you</span>
        <span className="text-fg-subtle" title={TIER_DESCRIPTION[step.tier]}>
          {tierWord(step)}
        </span>
        <span className="ml-auto shrink-0 text-fg-subtle num">
          <RelativeTime ts={entry.at} />
        </span>
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <h3 id={headingId} className="text-body font-medium text-fg">
          {step.title}
        </h3>
        <p className="text-ui text-fg-muted">{step.approval?.why ?? 'Your settings ask before this change.'}</p>
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <Pressable onClick={() => setShowInput((o) => !o)} aria-expanded={showInput} className="flex h-control-sm w-auto max-w-full items-center gap-2 self-start whitespace-nowrap rounded-control text-caption font-medium text-fg-muted hover:text-fg">
          <ChevronRight className={cn('size-3.5 transition-transform duration-(--duration-fast)', showInput && 'rotate-90')} aria-hidden />
          What it sends
          {!showInput && summary && <span className="ml-1 hidden min-w-0 max-w-80 truncate font-mono font-normal text-fg-subtle sm:inline">{summary}</span>}
        </Pressable>
        {showInput && <pre className="motion-arrive max-h-48 overflow-auto rounded-control bg-sunken px-3 py-2 font-mono text-caption text-fg-muted">{stepInputJson(step.input)}</pre>}
      </div>
      {s.canAnswer ? (
        <div className="flex min-w-0 flex-col gap-3">
          <Input aria-label="Note for the assistant" placeholder="Add a note" value={s.note} onChange={(ev) => s.setNote(ev.target.value)} maxLength={2000} />
          {s.error && (
            <p role="alert" className="text-ui text-status-danger-ink">
              {s.error}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="secondary" size="sm" loading={s.busy === 'deny'} disabled={!!s.busy || !step.approval?.id} onClick={() => void s.answer('deny')}>
              Deny step
            </Button>
            <Button variant="primary" size="sm" loading={s.busy === 'allow'} disabled={!!s.busy || !step.approval?.id} onClick={() => void s.answer('allow')}>
              Approve step
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-ui text-fg-muted">{tierBlockedText(step)} Ask one to answer it here or in the Inbox.</p>
      )}
    </section>
  );
}
